/**
 * vitam-paws-supply.js
 * Custom element controller for the Vitam Paws weight + supply selector.
 */

(function () {
  if (customElements.get("vitam-paws-two-step")) return;

  // teaspoons per day, indexed by weight key.
  const DOSE_PER_WEIGHT = {
    small: 0.5, // up to 10 kg
    medium: 1, // 10-20 kg
    large: 1.25, // 20-40 kg
    xlarge: 1.5, // >40 kg
  };

  const WEIGHT_LABEL = {
    small: "up to 10 kg",
    medium: "10-20 kg",
    large: "20-40 kg",
    xlarge: "over 40 kg",
  };

  // Days per 200 ml bottle, from the Vitam Paws feeding chart.
  const DAYS_PER_200ML_BY_WEIGHT = {
    small: 80,
    medium: 40,
    large: 30,
    xlarge: 25,
  };

  const RECOMMENDED_ML_BY_WEIGHT = {
    small: 200,
    medium: 200,
    large: 400,
    xlarge: 800,
  };

  function formatDose(teaspoons) {
    if (teaspoons === 0.5) return "0.5 teaspoon";
    if (teaspoons === 1) return "1 teaspoon";
    return `${teaspoons} teaspoons`;
  }

  function formatMoney(cents) {
    const rupees = Math.round(cents / 100);
    return "₹" + rupees.toLocaleString("en-IN");
  }

  function extractBottleMl(variantTitle) {
    if (!variantTitle) return null;
    const title = String(variantTitle);
    let match = title.match(/(\d{2,4})\s*[-x×*]?\s*m\s*l\b/i);
    if (match) return parseInt(match[1], 10);
    match = title.match(/(\d{2,4})\s*[-x×*]?\s*(gm|gms|gram|grams)\b/i);
    if (match) return parseInt(match[1], 10);
    match = title.match(/^\s*(\d{2,4})\b/);
    if (match) return parseInt(match[1], 10);
    console.warn(
      "[vitam-paws-two-step] could not parse bottle size from variant title:",
      variantTitle,
    );
    return null;
  }

  function extractSupplyLabel(variantTitle) {
    if (!variantTitle) return "";
    const ml = extractBottleMl(variantTitle);
    return ml != null ? `${ml} ML` : "";
  }

  function formatDuration(days) {
    const rounded = Math.max(1, Math.round(days));
    return rounded === 1 ? "1 day" : `${rounded} days`;
  }

  class VitamPawsTwoStep extends HTMLElement {
    constructor() {
      super();
      this.selectedWeight = null;
      this.selectedVariant = null;
      this._weightWasClicked = false;
      this._supplyWasClicked = false;
      this._abort = null;
    }

    connectedCallback() {
      this._abort = new AbortController();
      this.variants = this._readVariants();
      this.selectedVariant = this._findCheckedVariant();
      this._bindWeightButtons();
      this._bindVariantChanges();
      this._selectWeight("medium", { silent: true, initial: true });
      this._renderSupplyDurations();
      this._render();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this._abort = null;
    }

    _signal() {
      return this._abort ? this._abort.signal : undefined;
    }

    _readVariants() {
      const script = this.querySelector("script#productVariants");
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.warn(
          "[vitam-paws-two-step] productVariants JSON parse failed:",
          e,
        );
        return [];
      }
    }

    _findCheckedVariant() {
      const checked = this.querySelector(
        ".jounce-two-step__plan-input:checked",
      );
      if (!checked) return this.variants[0] || null;
      return (
        this.variants.find((v) => v.option1 === checked.value) ||
        this.variants[0] ||
        null
      );
    }

    _bindWeightButtons() {
      const signal = this._signal();
      this._weightButtons = this.querySelectorAll("[data-vitam-paws-weight]");
      this._weightButtons.forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            this._selectWeight(btn.dataset.vitamPawsWeight, {
              userInitiated: true,
            });
          },
          { signal },
        );
      });
    }

    _bindVariantChanges() {
      const signal = this._signal();
      this.querySelectorAll(".jounce-two-step__plan-input").forEach((input) => {
        input.addEventListener(
          "change",
          (e) => {
            if (!e.target.checked) return;
            if (!e.target.dataset.vitamPawsAutoSelected) {
              this._supplyWasClicked = true;
            } else {
              delete e.target.dataset.vitamPawsAutoSelected;
            }
            this.selectedVariant =
              this.variants.find((v) => v.option1 === e.target.value) ||
              this.selectedVariant;
            this._render();
          },
          { signal },
        );
      });
    }

    _selectWeight(key, opts) {
      opts = opts || {};
      this.selectedWeight = key;
      if (opts.userInitiated) this._weightWasClicked = true;

      const buttons =
        this._weightButtons ||
        this.querySelectorAll("[data-vitam-paws-weight]");
      buttons.forEach((button) => {
        const active = button.dataset.vitamPawsWeight === key;
        button.setAttribute("aria-checked", active ? "true" : "false");
        button.classList.toggle("is-selected", active);
      });

      const hiddenAttr = this.querySelector("[data-vitam-paws-weight-attr]");
      if (hiddenAttr) {
        hiddenAttr.value = this._weightWasClicked
          ? WEIGHT_LABEL[key] || key
          : "";
      }

      const recommendedMl = RECOMMENDED_ML_BY_WEIGHT[key];
      if (recommendedMl && !this._supplyWasClicked && opts.userInitiated) {
        const targetVariant = this.variants.find(
          (variant) => extractBottleMl(variant.title) === recommendedMl,
        );
        if (targetVariant) {
          const radio = this._findRadioForVariant(targetVariant);
          if (radio && !radio.checked) {
            radio.dataset.vitamPawsAutoSelected = "1";
            radio.checked = true;
            this.selectedVariant = targetVariant;
            if (!opts.silent) {
              radio.dispatchEvent(new Event("input", { bubbles: true }));
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (radio) {
            this.selectedVariant = targetVariant;
          }
        }
      }

      this._renderSupplyDurations();
      this._render();
    }

    _findRadioForVariant(variant) {
      if (!variant || variant.option1 == null) return null;
      const radios = this.querySelectorAll(".jounce-two-step__plan-input");
      for (const radio of radios) {
        if (radio.value === variant.option1) return radio;
      }
      return null;
    }

    _durationDaysFor(weightKey, bottleMl) {
      const baseDays = DAYS_PER_200ML_BY_WEIGHT[weightKey];
      if (!baseDays || !bottleMl) return null;
      return baseDays * (bottleMl / 200);
    }

    _renderSupplyDurations() {
      const weightKey = this.selectedWeight || "medium";
      this.querySelectorAll("[data-vitam-paws-supply-days]").forEach((node) => {
        const bottleMl = parseInt(node.dataset.vitamPawsSupplyMl, 10);
        const days = this._durationDaysFor(weightKey, bottleMl);
        node.textContent = days ? `${Math.round(days)} days` : "";
      });
    }

    _render() {
      const card = this.querySelector("[data-vitam-paws-info-card]");
      if (!card || !this.selectedVariant) return;

      const variant = this.selectedVariant;
      const bottleMl = extractBottleMl(variant.title);
      const supplyLabel = extractSupplyLabel(variant.title);
      const dosePerDay = this.selectedWeight
        ? DOSE_PER_WEIGHT[this.selectedWeight]
        : null;

      const $supply = card.querySelector('[data-vitam-paws-info="supply"]');
      const $dose = card.querySelector('[data-vitam-paws-info="dose"]');
      const $duration = card.querySelector('[data-vitam-paws-info="duration"]');
      const $price = card.querySelector('[data-vitam-paws-info="price"]');
      const $compare = card.querySelector('[data-vitam-paws-info="compare"]');
      const $save = card.querySelector('[data-vitam-paws-info="save"]');

      if ($supply && supplyLabel)
        $supply.textContent = "Includes: " + supplyLabel;
      if ($price) $price.textContent = formatMoney(variant.price);

      const saveAmt = (variant.compare_at_price || 0) - variant.price;
      if ($compare) {
        $compare.textContent =
          saveAmt > 0 ? formatMoney(variant.compare_at_price) : "";
      }
      if ($save) {
        $save.textContent = saveAmt > 0 ? "Save " + formatMoney(saveAmt) : "";
      }

      if (dosePerDay && bottleMl) {
        const days = this._durationDaysFor(this.selectedWeight, bottleMl);
        if ($dose) {
          $dose.textContent = `${formatDose(dosePerDay)} per day  ·  for pets ${WEIGHT_LABEL[this.selectedWeight]}`;
        }
        if ($duration) {
          $duration.textContent = days
            ? formatDuration(days)
            : "Select your pet's weight";
        }
        card.classList.add("is-ready");
      } else {
        if ($dose) $dose.textContent = "Select your pet's weight";
        if ($duration) $duration.textContent = "Select your pet's weight";
        card.classList.remove("is-ready");
      }
    }
  }

  customElements.define("vitam-paws-two-step", VitamPawsTwoStep);
})();
